import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, CardContent } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'csv-to-json'
const MAX_UPLOAD_BYTES = 1024 * 1024
const AUTO_PREVIEW_BYTES = 128 * 1024

type DelimiterMode = 'auto' | 'comma' | 'semicolon' | 'tab' | 'pipe'
type DelimiterChar = ',' | ';' | '\t' | '|'
type ParseResult = { ok: true; rows: string[][] } | { ok: false; error: string }
type ConvertResult = {
  ok: true
  json: string
  delimiter: DelimiterChar
  rows: number
  columns: number
} | { ok: false; error: string }

const DELIMITER_OPTIONS: DelimiterMode[] = ['auto', 'comma', 'semicolon', 'tab', 'pipe']
const DELIMITERS: Record<Exclude<DelimiterMode, 'auto'>, DelimiterChar> = {
  comma: ',',
  semicolon: ';',
  tab: '\t',
  pipe: '|',
}

function parseCsv(input: string, delimiter: DelimiterChar): ParseResult {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  for (let index = 0; index < input.length; index++) {
    const char = input[index]

    if (inQuotes) {
      if (char === '"') {
        if (input[index + 1] === '"') {
          field += '"'
          index += 1
        } else {
          inQuotes = false
        }
      } else {
        field += char
      }
      continue
    }

    if (char === '"' && field.length === 0) {
      inQuotes = true
      continue
    }

    if (char === delimiter) {
      row.push(field)
      field = ''
      continue
    }

    if (char === '\r' || char === '\n') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''

      if (char === '\r' && input[index + 1] === '\n') {
        index += 1
      }
      continue
    }

    field += char
  }

  if (inQuotes) {
    return { ok: false, error: 'unclosed-quote' }
  }

  if (field.length > 0 || row.length > 0 || !/[\r\n]$/.test(input)) {
    row.push(field)
    rows.push(row)
  }

  return { ok: true, rows }
}

function detectDelimiter(input: string): DelimiterChar {
  const candidates = Object.values(DELIMITERS)
  let best = candidates[0]!
  let bestScore = -1

  for (const delimiter of candidates) {
    const parsed = parseCsv(input, delimiter)
    if (!parsed.ok) continue

    const nonEmptyRows = parsed.rows.filter((row) => row.some((cell) => cell.trim() !== ''))
    const columnCounts = nonEmptyRows.map((row) => row.length)
    const maxColumns = Math.max(0, ...columnCounts)
    const commonRows = columnCounts.filter((count) => count === maxColumns).length
    const score = maxColumns * 100 + commonRows * 10 + nonEmptyRows.length

    if (score > bestScore) {
      best = delimiter
      bestScore = score
    }
  }

  return best
}

function uniqueHeaders(headerRow: string[], columnCount: number): string[] {
  const used = new Map<string, number>()

  return Array.from({ length: columnCount }, (_, index) => {
    const rawHeader = headerRow[index]?.trim()
    const base = rawHeader || `column_${index + 1}`
    const seen = used.get(base) ?? 0
    used.set(base, seen + 1)
    return seen === 0 ? base : `${base}_${seen + 1}`
  })
}

function convertCsv(input: string, delimiterMode: DelimiterMode, hasHeader: boolean): ConvertResult {
  if (!input.trim()) return { ok: false, error: 'empty' }

  const delimiter = delimiterMode === 'auto'
    ? detectDelimiter(input)
    : DELIMITERS[delimiterMode]
  const parsed = parseCsv(input, delimiter)
  if (!parsed.ok) return { ok: false, error: parsed.error }

  const rows = parsed.rows.filter((row) => row.some((cell) => cell.trim() !== ''))
  if (rows.length === 0) return { ok: false, error: 'empty' }

  const columnCount = Math.max(...rows.map((row) => row.length))
  const value = hasHeader
    ? rows.slice(1).map((row) => {
      const record: Record<string, string> = {}
      const headers = uniqueHeaders(rows[0] ?? [], columnCount)
      headers.forEach((header, index) => {
        record[header] = row[index] ?? ''
      })
      return record
    })
    : rows

  return {
    ok: true,
    json: JSON.stringify(value, null, 2),
    delimiter,
    rows: hasHeader ? Math.max(0, rows.length - 1) : rows.length,
    columns: columnCount,
  }
}

export function CsvToJsonPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const [input, setInput] = useState('')
  const [delimiterMode, setDelimiterMode] = useState<DelimiterMode>('auto')
  const [hasHeader, setHasHeader] = useState(true)
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [resultStats, setResultStats] = useState<{
    delimiter: DelimiterChar
    rows: number
    columns: number
  } | null>(null)

  const inputStats = useMemo(() => {
    return {
      chars: input.length,
      lines: input ? input.split(/\r\n|\r|\n/).length : 0,
      detectedDelimiter: input.trim() ? detectDelimiter(input) : null,
    }
  }, [input])
  const inputBytes = useMemo(() => new TextEncoder().encode(input).byteLength, [input])
  const requiresExplicitConvert = inputBytes > AUTO_PREVIEW_BYTES

  const setSource = useCallback((value: string) => {
    setInput(value)
    setOutput('')
    setError(null)
    setResultStats(null)
  }, [])

  const handleFileUpload = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.size > MAX_UPLOAD_BYTES) {
      setError(t.csvToJson.errors.fileTooLarge)
      setOutput('')
      setResultStats(null)
      toolError(UTILITY, 'file-too-large', { action: 'upload' })
      return
    }

    try {
      setSource(await file.text())
    } catch {
      setError(t.csvToJson.errors.fileRead)
      setOutput('')
      setResultStats(null)
      toolError(UTILITY, 'file-read', { action: 'upload' })
    }
  }, [setSource, t.csvToJson.errors.fileRead, t.csvToJson.errors.fileTooLarge])

  const handleConvert = useCallback(() => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setResultStats(null)
      return
    }

    const start = performance.now()
    toolInvoke(UTILITY, { delimiter: delimiterMode, header: hasHeader })

    const result = convertCsv(input, delimiterMode, hasHeader)
    if (result.ok) {
      setOutput(result.json)
      setError(null)
      setResultStats({
        delimiter: result.delimiter,
        rows: result.rows,
        columns: result.columns,
      })
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        delimiter: delimiterMode,
        header: hasHeader,
      })
    } else {
      const message = result.error === 'unclosed-quote'
        ? t.csvToJson.errors.unclosedQuote
        : t.csvToJson.errors.empty
      setOutput('')
      setResultStats(null)
      setError(message)
      toolError(UTILITY, result.error, { delimiter: delimiterMode, header: hasHeader })
    }
  }, [delimiterMode, hasHeader, input, t.csvToJson.errors.empty, t.csvToJson.errors.unclosedQuote])

  useEffect(() => {
    if (requiresExplicitConvert) return
    const timeout = window.setTimeout(handleConvert, 250)
    return () => window.clearTimeout(timeout)
  }, [handleConvert, requiresExplicitConvert])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleDownload = useCallback(() => {
    if (!output) return
    const blob = new Blob([output], { type: 'application/json;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'data.json'
    link.click()
    URL.revokeObjectURL(url)
  }, [output])

  const handleClear = useCallback(() => {
    setSource('')
  }, [setSource])

  return (
    <ToolPageShell toolKey="csvToJson" width="wide">
      <CodeWorkspace
        error={error}
        controls={(
          <div className="grid gap-4 lg:grid-cols-[1fr_auto_auto] lg:items-end">
            <div>
              <label
                htmlFor="csv-delimiter"
                className="mb-1.5 block text-xs font-medium text-muted-foreground"
              >
                {t.csvToJson.delimiter}
              </label>
              <select
                id="csv-delimiter"
                value={delimiterMode}
                onChange={(event) => {
                  setDelimiterMode(event.target.value as DelimiterMode)
                  setOutput('')
                  setError(null)
                  setResultStats(null)
                }}
                className="h-10 w-full rounded-md border bg-background px-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring lg:w-56"
              >
                {DELIMITER_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {t.csvToJson.delimiters[item]}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex min-h-10 items-center gap-2 rounded-md border bg-card px-3 text-sm shadow-sm">
              <input
                type="checkbox"
                checked={hasHeader}
                onChange={(event) => {
                  setHasHeader(event.target.checked)
                  setOutput('')
                  setError(null)
                  setResultStats(null)
                }}
                className="h-4 w-4 accent-primary"
              />
              <span>{t.csvToJson.headerRow}</span>
            </label>

            {requiresExplicitConvert && (
              <Button data-code-run onClick={handleConvert} disabled={!input.trim()}>
                {t.csvToJson.convertLarge}
              </Button>
            )}
          </div>
        )}
        input={{
          title: t.csvToJson.input,
          value: input,
          clear: { label: t.csvToJson.clear, onClick: handleClear },
          onChange: setSource,
          placeholder: t.csvToJson.inputPlaceholder,
          ariaLabel: t.csvToJson.input,
          actions: (
            <>
              <label className="inline-flex cursor-pointer items-center rounded-md border bg-background px-3 py-1.5 text-sm font-medium shadow-sm transition-colors hover:bg-accent">
                {t.csvToJson.upload}
                <input
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  className="sr-only"
                  onChange={handleFileUpload}
                />
              </label>
            </>
          ),
          stats: [
            {
              label: t.csvToJson.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.csvToJson.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
            ...(inputStats.detectedDelimiter
              ? [{
                label: t.csvToJson.detected,
                value: t.csvToJson.delimiterSymbols[inputStats.detectedDelimiter],
              }]
              : []),
          ],
        }}
        output={{
          title: t.csvToJson.output,
          value: output,
          readOnly: true,
          placeholder: t.csvToJson.outputPlaceholder,
          ariaLabel: t.csvToJson.output,
          actions: (
            <>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copied ? t.csvToJson.copied : t.csvToJson.copy}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDownload}>
                {t.csvToJson.download}
              </Button>
            </>
          ),
          status: requiresExplicitConvert ? (
            <div className="border-t bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
              {t.csvToJson.autoPreviewLimit}
            </div>
          ) : undefined,
        }}
      />

      {resultStats && (
        <section className="mt-6 grid gap-4 sm:grid-cols-3">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.csvToJson.rows}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {resultStats.rows.toLocaleString(localeCode)}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.csvToJson.columns}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {resultStats.columns.toLocaleString(localeCode)}
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="text-sm font-medium tracking-normal">{t.csvToJson.delimiterUsed}</div>
              <div className="mt-1 text-2xl font-bold tabular-nums">
                {t.csvToJson.delimiterSymbols[resultStats.delimiter]}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.csvToJson.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.csvToJson.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.csvToJson.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.csvToJson.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
